/**
 * Resupport — the shared data model.
 *
 * This file is the contract between the calc engine (core), the app, the sync worker and the
 * data pipeline. Change it deliberately: everything downstream depends on it.
 *
 * THE BIG IDEA. Recipes and plans ask for *kinds* of food ("100 g of noodles", "a veg: carrot,
 * capsicum or zucchini"), not for barcodes. A shopping list line says what will satisfy it, and
 * in the shop any product whose category falls under that kind fills the line. A category tree
 * makes "any noodle" mean ramen, soba, rice noodles or hokkien alike.
 *
 * UNITS. Grams for mass, kcal for energy (kJ stored alongside where a label gave it, converted
 * only in `units.ts`), millilitres for water. Money is always an amount plus an ISO 4217
 * currency code; nothing converts between currencies.
 */

export const KJ_PER_KCAL = 4.184

/** ISO 8601 date (YYYY-MM-DD) or full timestamp. Compare with `timeOf()`, never as strings. */
export type IsoTime = string

// ---------------------------------------------------------------------------
// Regions and availability
// ---------------------------------------------------------------------------

/**
 * ISO 3166-1 alpha-2 country code ("AU", "ES", "JP"), optionally with an ISO 3166-2
 * subdivision ("US-CA", "ES-GA"). Lookups fall back from subdivision to country.
 */
export type RegionCode = string

export interface Region {
  code: RegionCode
  name: string
  /** ISO 4217, e.g. "AUD", "EUR", "JPY". */
  currency: string
  /** Which energy unit local nutrition panels lead with. Drives the default display unit. */
  label_energy: 'kJ' | 'kcal' | 'both'
  /** Long-distance trails here, for the region picker ("Camino Francés", "Te Araroa"). */
  trails?: string[]
  /** Typical shops a hiker meets, smallest first, for the availability scale's examples. */
  shop_examples?: string[]
}

/**
 * How easy something is to find in a region. A sliding scale from "everywhere" to "only in
 * specialty stores":
 *
 *   5  everywhere      — any shop at all, incl. general stores, petrol stations, village shops
 *   4  common          — any supermarket and most small grocers / minimarkets
 *   3  supermarket     — mid-size and large supermarkets only
 *   2  specialty aisle — big-city hypermarkets' international aisle, outdoor, health-food or
 *                        ethnic grocers
 *   1  specialty only  — specialist shops in cities, or online; post it or carry it in
 *   0  not available   — effectively absent; don't plan on it
 *
 * Unknown is `undefined`, never 0.
 */
export type AvailabilityLevel = 0 | 1 | 2 | 3 | 4 | 5

/** Price relative to what a local would expect for a staple, not an absolute price. */
export type PriceLevel = 'cheap' | 'typical' | 'pricey' | 'very-pricey'

export interface AvailabilityRating {
  level: AvailabilityLevel
  price?: PriceLevel
  /** e.g. "Only Yatekomo cup noodles in most Mercadona; packet ramen in Asian grocers". */
  note?: string
  /** Curated ships with the app; user is this device's own rating; community came via sync. */
  source: 'curated' | 'user' | 'community'
  /** How many people's ratings a community figure is drawn from. */
  votes?: number
  updated_at?: IsoTime
}

/**
 * `data/availability.json`: category id → region → rating. Ratings inherit DOWN the category
 * tree (a rating on "noodles" covers "rice-noodles" until rice noodles get their own), and a
 * food may override its category for one region (`Food.availability`).
 */
export type AvailabilityTable = Record<string, Record<RegionCode, AvailabilityRating>>

// ---------------------------------------------------------------------------
// Categories — the taxonomy that makes "any noodle" work
// ---------------------------------------------------------------------------

/** Where a thing lives in a shop, for grouping the shopping list. Inherited down the tree. */
export type Aisle =
  | 'produce'
  | 'bakery'
  | 'dairy'
  | 'meat'
  | 'pantry'
  | 'pasta-rice'
  | 'international'
  | 'breakfast'
  | 'snacks'
  | 'nuts-dried-fruit'
  | 'confectionery'
  | 'drinks'
  | 'spices-condiments'
  | 'health'
  | 'outdoor'
  | 'other'

/** What a food is typically eaten as. Hints only: filters and defaults, never the maths. */
export type Role = 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'drink' | 'ingredient'

export interface Category {
  /** kebab-case, globally unique, stable forever (recipes and baskets point at it). */
  id: string
  name: string
  /** Parent category id; null for a top-level group. The tree is acyclic. */
  parent: string | null
  /**
   * Search terms and other-language names, lower-case: "ramen", "cup noodles", "fideos".
   * Also used to auto-categorise imported products by name.
   */
  aliases?: string[]
  aisle?: Aisle
  roles?: Role[]
  /**
   * The id of the generic "best guess" food for this category: what a plan assumes when a
   * recipe says "any noodle" and no one has picked yet. Absent → walk down to the first child
   * that has one, then up to the nearest ancestor that has one.
   */
  generic?: string
  /** Salt, spices, tea: shows on lists, contributes no energy. Inherited. */
  non_caloric?: boolean
  description?: string
}

// ---------------------------------------------------------------------------
// Foods
// ---------------------------------------------------------------------------

/**
 * `generic` — a typical member of a category with representative nutrition ("Instant noodles,
 *             fried, dry"), from composition tables. Never has a barcode.
 * `product` — a specific thing on a shelf ("Indomie Mi Goreng 85 g"), from a label, a store,
 *             Open Food Facts or a person.
 */
export type FoodKind = 'generic' | 'product'

export type DataSource =
  | 'curated' // hand-built for Resupport (generic foods, typical packs)
  | 'usda' // USDA FoodData Central
  | 'ausnut' // FSANZ AUSNUT / AFCD
  | 'cofid' // UK McCance & Widdowson
  | 'openfoodfacts'
  | 'store' // a supermarket's own site
  | 'panel-photo'
  | 'sheet-import' // a spreadsheet someone published or uploaded
  | 'manual'

export interface Pack {
  size_g: number
  /** Null for bulk packs with no stated serve count. */
  serves?: number | null
  /** As on the shelf: "6 pack", "750 g bag", "5 × 85 g". */
  label?: string
}

export interface Price {
  amount: number
  /** ISO 4217. */
  currency: string
  /** For one `Food.pack` unless `per_g` is given. */
  per_g?: number
  store?: string
  region?: RegionCode
  as_of: IsoTime
  on_special?: boolean
}

/** Per 100 g. All optional: a two-number shop-floor entry is a valid food. */
export interface Nutrition {
  protein_g?: number
  fat_g?: number
  saturated_fat_g?: number
  carb_g?: number
  sugar_g?: number
  fibre_g?: number
  sodium_mg?: number
  potassium_mg?: number
}

export type DietFlag =
  | 'vegetarian'
  | 'vegan'
  | 'gluten-free'
  | 'dairy-free'
  | 'nut-free'
  | 'halal'
  | 'kosher'

export type PrepMethod = 'none' | 'cold-soak' | 'just-add-boiling' | 'simmer'

export interface Prep {
  method: PrepMethod
  /** Water to rehydrate or cook 100 g, ml. */
  water_ml_per_100g?: number
  minutes?: number
}

/**
 * An atomic item: a generic best guess or a real product.
 *
 * MINIMUM VIABLE RECORD: id, name, kind, category, kcal_per_100g, source, verified. The
 * shop-floor quick add must be able to mint one from a name, a category and two numbers.
 */
export interface Food {
  id: string
  name: string
  kind: FoodKind
  /** Category id. Every food has exactly one; it is what lets the shop match it to a line. */
  category: string
  brand?: string

  kcal_per_100g: number
  /** Stored when the source gave kJ, so a kJ label round-trips exactly. */
  kj_per_100g?: number
  nutrition?: Nutrition

  /** Mass of one serve as the label/source defines it. */
  serve_g?: number
  serve_label?: string
  /** Primary pack. For a generic food, the most typical pack size in shops. */
  pack?: Pack
  prices?: Price[]
  barcodes?: string[]
  /** Where this product is sold. Absent on generic foods (they are everywhere the category is). */
  regions?: RegionCode[]
  /** Per-region override of the category's availability, for a specific product. */
  availability?: Record<RegionCode, AvailabilityRating>

  diet?: DietFlag[]
  prep?: Prep
  /** Shelf-stable without refrigeration for a week of carrying. Absent = unknown. */
  shelf_stable?: boolean

  source: DataSource
  /** The source's own id: FDC id, OFF code, AUSNUT key, spreadsheet name + row. */
  source_ref?: string
  /** A human has checked the numbers. Importers write false. */
  verified: boolean
  notes?: string
  updated_at?: IsoTime
  /** Soft delete, so a delete syncs. */
  deleted?: boolean
  /**
   * USER LAYER ONLY: a reset marker, `{ id, reset: true, updated_at }`, carrying no other
   * fields. It means "no overlay here": `mergeCatalog` drops it and keeps the shipped record,
   * so the shipped one shows again and later data updates reach it. It is a record (not an
   * absence) so the reset syncs and beats older user copies on other devices by updated_at.
   */
  reset?: boolean
}

// ---------------------------------------------------------------------------
// Recipes — with open slots
// ---------------------------------------------------------------------------

/** What can fill a requirement: one exact food, or anything in (or under) a category. */
export type Match = { food: string } | { category: string }

/**
 * One line of a recipe, per serve. `options` decides how open it is:
 *
 *   [{ food: 'peanut-butter-smooth' }]               fixed: exactly this
 *   [{ category: 'noodles' }]                        any noodle at all
 *   [{ category: 'carrot' }, { category: 'capsicum' },
 *    { category: 'zucchini' }]                       a choice: one of these, decided later
 *
 * Grams are per option's own weight — 80 g of noodles is 80 g whichever noodle. Energy for a
 * plan is ESTIMATED from the default option's generic food; the basket records the real thing.
 */
export interface Ingredient {
  /** Stable within the recipe, so a plan can record which option it picked. */
  id: string
  /** What to call an open slot: "Noodles", "A veg", "Cheese". Falls back to the match name. */
  label?: string
  g: number
  /** At least one. Order matters: `default` indexes into it. */
  options: Match[]
  /** Which option the estimate uses until someone picks. Default 0. */
  default?: number
  /** Salt, spices, stock powder: listed, carries no energy. */
  non_caloric?: boolean
  /** Nice to have. Still listed, flagged optional, still counted in the estimate. */
  optional?: boolean
  note?: string
}

export type MealSlot = 'breakfast' | 'lunch' | 'dinner'

export interface Recipe {
  id: string
  name: string
  /** Which meal it is for; 'snack' recipes (trail mix) go in the day template. */
  meal?: MealSlot | 'snack'
  ingredients: Ingredient[]
  /** Measured energy that beats the sum of parts. Use sparingly. */
  kcal_per_serve_override?: number
  /** Method: bagging at home and cooking on trail. Markdown-lite (line breaks only). */
  method?: string
  prep?: Prep
  diet?: DietFlag[]
  tags?: string[]
  /** Who/where this came from: "Andrew Skurka", "my own". */
  credit?: string
  notes?: string
  /** Ships with the app vs. made by this person. */
  source: 'curated' | 'user' | 'sheet-import'
  updated_at?: IsoTime
  deleted?: boolean
  /** USER LAYER ONLY: a reset marker, as `Food.reset`. */
  reset?: boolean
}

// ---------------------------------------------------------------------------
// Trips and sections
// ---------------------------------------------------------------------------

/** What a plan line points at. A category is "3 bars a day, any bar". */
export type ItemRef = { food: string } | { recipe: string } | { category: string }

/**
 * A pick made at planning time for an open slot: ingredient id → the match to use. Picking a
 * category narrows ("noodles" → "rice-noodles"); picking a food fixes it. Unpicked slots stay
 * open and get filled in the shop.
 */
export type Picks = Record<string, Match>

export type ResupplyMethod = 'carry' | 'food-drop' | 'post' | 'buy-in-town'

/** Stated per section, never derived from days (a 5.5-day leg decides its own). */
export interface SlotCounts {
  breakfast: number
  lunch: number
  dinner: number
}

/** Every day: this many serves of this item. */
export interface DayLine {
  id: string
  item: ItemRef
  serves_per_day: number
  /** For a recipe item. For a food/category item, use `item` itself to narrow. */
  picks?: Picks
  note?: string
}

/** Across the section: `count` of this item for this meal. */
export interface MealLine {
  id: string
  slot: MealSlot
  item: ItemRef
  /** Defaults to the slot count when absent. */
  count?: number
  picks?: Picks
  note?: string
}

/** One thing in the trolley. Everything about it is a snapshot from the moment it went in. */
export interface BasketEntry {
  id: string
  /** Null for a quick entry never saved as a food. */
  food: string | null
  /** Display name; always set. */
  label: string
  /**
   * Category snapshot. Set on EVERY entry that has one — a quick entry picks one ("noodles,
   * 120 g, 540 kcal") — because this is what lets it fill an open line.
   */
  category?: string
  units: number
  kcal_each: number
  grams_each: number
  price_each?: { amount: number; currency: string }
  /** Absolute grams (mg for sodium) for ONE unit, not per 100 g. */
  macros_each?: Nutrition
  /** The shopping line this was bought for (ShoppingLine.key), when the person said so. */
  for_line?: string
  /** "Counts as a dinner" for an off-plan item. */
  meal?: MealSlot | 'snack'
  added_at: IsoTime
  entry: 'search' | 'barcode' | 'photo' | 'quick' | 'from-list'
}

export type SectionStatus = 'planned' | 'shopping' | 'shopped' | 'walked'

export interface Section {
  id: string
  name: string
  /** May be fractional. */
  days: number
  method: ResupplyMethod
  /** The goal anchor, per section: terrain and appetite vary. */
  kcal_per_day: number
  slots: SlotCounts
  /** Where the food is bought (or posted from). Drives availability warnings and currency. */
  region?: RegionCode
  /** Free text: "Mercadona Sarria", "Four Square Twizel". */
  shop?: string
  /** ISO date the food has to be bought/posted by. */
  buy_by?: string
  /**
   * How many people this food is for. Default 1. The goal (`kcal_per_day`) and the slot counts
   * stay PER PERSON; grams, packs, packing bags and cost scale with it.
   */
  people?: number
  /**
   * Hours walked a day on this section, for the kcal/day estimator. Absent: the person's
   * profile figure (app settings).
   */
  hours_per_day?: number

  day_lines: DayLine[]
  meal_lines: MealLine[]

  status: SectionStatus
  basket: BasketEntry[]
  /** Shopping-list lines the person marked "not stocked here", by key. */
  not_here?: string[]
  /** Written after walking it. The most valuable field in the file. */
  review?: string
  copied_from?: string
  notes?: string
  updated_at?: IsoTime
}

export interface Trip {
  id: string
  name: string
  /** Default region for new sections. */
  region?: RegionCode
  sections: Section[]
  notes?: string
  updated_at?: IsoTime
  /** When the trip's own fields or section order last changed (sync merges by section). */
  info_updated_at?: IsoTime
  /** Section id -> when it was removed, so a merge does not bring it back (`tripsync.ts`). */
  removed_sections?: Record<string, IsoTime>
  deleted?: boolean
}

// ---------------------------------------------------------------------------
// The catalog — everything the engine needs to resolve ids
// ---------------------------------------------------------------------------

export interface Catalog {
  categories: Map<string, Category>
  foods: Map<string, Food>
  recipes: Map<string, Recipe>
  availability: AvailabilityTable
  regions: Map<RegionCode, Region>
}

// ---------------------------------------------------------------------------
// Engine output
// ---------------------------------------------------------------------------

/** Why grams are wanted. Every gram on the list stays traceable to its source. */
export interface Contribution {
  /** "Snacks", "Dinner: Peanut noodles". */
  label: string
  /** DayLine/MealLine id. */
  line_id: string
  recipe?: string
  ingredient?: string
  grams: number
  kcal: number
}

/**
 * One line of the shopping list. Lines are merged by `key`: everything in the plan that would
 * be satisfied by the same set of matches becomes one number.
 */
export interface ShoppingLine {
  /** Canonical, stable: sorted matches joined, e.g. "c:noodles" or "c:capsicum|c:carrot". */
  key: string
  label: string
  /** What fills this line in the shop. */
  accepts: Match[]
  /** True when more than one product could fill it: a category match or a choice. */
  open: boolean
  /** The food the estimate assumed (the default option's generic, or the exact food). */
  assumed_food: string
  grams: number
  /** Estimated from `assumed_food`. Exact when not `open`. */
  kcal: number
  packs: number
  pack_size_g: number
  surplus_g: number
  cost?: { amount: number; currency: string }
  aisle: Aisle
  non_caloric: boolean
  optional: boolean
  /** In the section's region, best over everything the line accepts. */
  availability?: AvailabilityRating
  contributions: Contribution[]
}

export interface Totals {
  kcal: number
  grams: number
  kcal_per_g: number
  cost?: { amount: number; currency: string }
}

export interface MacroTotals {
  protein_g: number
  fat_g: number
  carb_g: number
  fibre_g: number
  sugar_g: number
  sodium_mg: number
  /** Share of energy, 0–1, at 4/9/4 kcal per g. Absent with no energy. */
  energy_share?: { protein: number; fat: number; carb: number }
  /** Some caloric line had no macros: these are a floor. */
  partial: boolean
}

export interface SlotCoverage {
  slot: MealSlot
  required: number
  planned: number
}

export interface PackingInstruction {
  line_id: string
  recipe: string
  name: string
  bags: number
  serve_g: number
  kcal_per_serve: number
  components: { label: string; accepts: Match[]; g: number; non_caloric: boolean; open: boolean }[]
}

export interface Warning {
  level: 'info' | 'warn' | 'error'
  code: string
  message: string
  line_id?: string
  key?: string
  /** The meal a coverage warning is about (`slot-short`). */
  slot?: MealSlot
  /** The region an availability or price warning is about (`hard-to-find`, `pricey`, ...). */
  region?: RegionCode
  /** The macro a `macro-off-target` warning is about. */
  macro?: 'protein' | 'fat' | 'carb'
}

/** A suggestion when something is hard to get where the section shops. */
export interface Substitute {
  /** The line it would replace. */
  key: string
  match: Match
  label: string
  availability: AvailabilityRating
  /** Why it is a fair swap: "same group: noodles", "similar energy". */
  reason: string
}

/** Energy by meal: where the calories come from (LDHFRT's Goals tab). */
export interface MealEnergy {
  /** 'snack' is every day line. */
  meal: MealSlot | 'snack'
  /** Over the whole section, everyone. */
  kcal: number
  /** Per person per day. */
  kcal_per_day: number
  /** Share of planned energy, 0–1. */
  share: number
}

/**
 * Nutrition targets a plan is checked against (settings; defaults in `DEFAULT_TARGETS`).
 * Shares are of macro energy at 4/9/4, 0–1.
 */
export interface PlanTargets {
  protein_share: number
  fat_share: number
  carb_share: number
  /** Sodium per 1000 kcal eaten. */
  sodium_mg_per_1000kcal: number
  /** Energy density the plan should reach. */
  kcal_per_g: number
  /** Planned energy within ± this share of the goal is on target (0.1 = ±10 %). */
  kcal_tolerance: number
  /** A macro share within ± this many points (0.05 = ±5 points) is on target. */
  macro_tolerance: number
}

/** One figure against its target. */
export interface TargetCheck {
  actual: number
  target: number
  /** Lowest and highest actual that is still on target. */
  low: number
  high: number
  status: 'low' | 'ok' | 'high'
}

/** The plan against `PlanTargets`, when targets were given. */
export interface TargetReport {
  protein?: TargetCheck
  fat?: TargetCheck
  carb?: TargetCheck
  /** mg per 1000 kcal. A floor when `macros.partial`. */
  sodium?: TargetCheck
  density?: TargetCheck
}

export interface SectionPlan {
  section_id: string
  /** Everyone's goal: days × kcal_per_day × people. */
  goal_kcal: number
  /** For everyone. */
  planned: Totals
  /** Per PERSON per day: compare with `kcal_per_day`. */
  per_day: Totals
  /** How many people the plan feeds (≥ 1). */
  people?: number
  /** Planned totals per person, when more than one. Cost per person lives here. */
  per_person?: Totals
  /** Energy by meal, breakfast / lunch / dinner / snacks. */
  by_meal?: MealEnergy[]
  /** Present when `planSection` was given targets. */
  targets?: TargetReport
  macros: MacroTotals
  /** planned.kcal − goal. Negative is short. */
  delta_kcal: number
  shopping: ShoppingLine[]
  coverage: SlotCoverage[]
  packing: PackingInstruction[]
  warnings: Warning[]
  substitutes: Substitute[]
}

/** Shop-mode state of one shopping line against the basket. */
export interface LineProgress {
  key: string
  need_g: number
  /** Grams in the basket counted toward this line (capped at need_g). */
  got_g: number
  got_kcal: number
  /** Basket entry ids filling it, with grams each contributed. */
  filled_by: { entry: string; grams: number }[]
  status: 'to-find' | 'partial' | 'done' | 'not-here'
}

export interface BasketTally {
  lines: LineProgress[]
  /** Entries (or parts of them) that filled no line. */
  extras: { entry: string; grams: number; kcal: number }[]
  basket: Totals
  macros: MacroTotals
  goal_kcal: number
  /**
   * The countdown. With plan lines: for each caloric line not yet done, its planned (estimated)
   * kcal minus the real kcal of what has filled it, floored at 0; done lines need nothing. So it
   * drops by the real energy of what went in. Without: goal − basket. Never negative.
   */
  still_to_buy_kcal: number
  coverage: SlotCoverage[]
}

// ---------------------------------------------------------------------------
// Import — spreadsheets and other people's food tables
// ---------------------------------------------------------------------------

/** A food not yet accepted into the catalog: from a sheet row, a barcode lookup or a photo. */
export interface FoodDraft {
  name: string
  brand?: string
  category?: string
  /** How sure the auto-categoriser was, 0–1. Below ~0.6 the UI asks. */
  category_confidence?: number
  kcal_per_100g?: number
  kj_per_100g?: number
  nutrition?: Nutrition
  serve_g?: number
  serve_label?: string
  kcal_per_serve?: number
  pack?: Pack
  price?: Price
  barcodes?: string[]
  /** Where the product is sold, when the source says ("off FR", a curated regional list). */
  regions?: RegionCode[]
  source: DataSource
  source_ref?: string
  /** Free text that came with it: a spreadsheet's Notes column. Becomes `Food.notes`. */
  notes?: string
  /** Problems found reading it: "kcal and kJ disagree", "no weight". */
  issues?: string[]
}

/** Which spreadsheet column holds which field. Detected, then confirmed by the person. */
export type SheetField =
  | 'name'
  | 'brand'
  | 'category'
  | 'kcal_per_100g'
  | 'kj_per_100g'
  | 'kcal_per_serve'
  | 'kj_per_serve'
  | 'kcal_per_oz'
  | 'serve_g'
  | 'serve_oz'
  | 'pack_g'
  | 'pack_oz'
  | 'serves_per_pack'
  | 'price'
  | 'protein_g'
  | 'fat_g'
  | 'carb_g'
  | 'sugar_g'
  | 'fibre_g'
  | 'sodium_mg'
  | 'barcode'
  | 'notes'
  | 'ignore'

export interface SheetMapping {
  header_row: number
  columns: Record<number, SheetField>
  /** Whether nutrient columns are per 100 g or per serve, when the sheet doesn't say. */
  nutrients_per: '100g' | 'serve'
  currency?: string
}

// ---------------------------------------------------------------------------
// Sync — what crosses the wire
// ---------------------------------------------------------------------------

/** Every syncable record kind. Last-writer-wins on `updated_at`, soft deletes. */
export type SyncKind = 'trip' | 'food' | 'recipe' | 'availability' | 'favourite'

/**
 * A starred food or recipe ("my usual foods", LDHFRT's Fav?). One record per star, so two
 * devices starring different things both win; un-starring is a soft delete. A star never
 * copies a shipped food into the user layer, so data updates still reach it.
 */
export interface Favourite {
  /** `${target}:${ref}`, e.g. "food:clif-bar", "recipe:peanut-noodles". */
  id: string
  target: 'food' | 'recipe'
  /** The food or recipe id. */
  ref: string
  updated_at?: IsoTime
  deleted?: boolean
}

export interface SyncRecord<T = unknown> {
  kind: SyncKind
  id: string
  updated_at: IsoTime
  deleted?: boolean
  data: T
}

/** A person's availability rating, shared (when they opt in) to build community figures. */
export interface AvailabilityVote {
  category: string
  food?: string
  region: RegionCode
  level: AvailabilityLevel
  price?: PriceLevel
  note?: string
  updated_at: IsoTime
  /** Withdraws this account's vote. */
  deleted?: boolean
}

/** Community ratings for one region, as the worker serves them. Overlay onto the curated table. */
export interface RegionRatings {
  region: RegionCode
  categories: Record<string, AvailabilityRating>
  foods: Record<string, AvailabilityRating>
  built_at?: IsoTime
}

export interface SyncRequest {
  /** A previous response's `server_time`; absent for a full pull. */
  since?: IsoTime | null
  cursor?: string
  push?: SyncRecord[]
}

export type PushOutcome = 'created' | 'updated' | 'unchanged' | 'stale' | 'rejected' | 'failed'

export interface PushResult {
  kind?: SyncKind
  id?: string
  outcome: PushOutcome
  /** The server's copy, when it won (`stale`) or clamped a future stamp. */
  record?: SyncRecord
  issues?: { path: string; message: string }[]
}

export interface SyncResponse {
  server_time: IsoTime
  records: SyncRecord[]
  /** More pages: repeat with the same `since`, this cursor and an empty push. */
  cursor?: string
  /** `results[i]` answers `push[i]`. */
  results: PushResult[]
}

export interface BarcodeLookup {
  draft: FoodDraft
  /** Open Food Facts category tags, for the app's auto-categoriser. */
  off_categories: string[]
  /** Canonical EAN-13. */
  code: string
  cached: boolean
  fetched_at: IsoTime
}

export interface PanelReading {
  draft: FoodDraft
  confidence: 'high' | 'medium' | 'low'
  model: string
  server_time: IsoTime
}
